import { motion } from "framer-motion";

interface MotoIconProps {
  isLoading?: boolean;
  isWheeling?: boolean;
  size?: "xs" | "sm" | "md";
}

export function MotoIcon({ isLoading = false, isWheeling = false, size = "md" }: MotoIconProps) {
  const sizeClass = size === "xs" ? "w-5 h-5" : size === "sm" ? "w-8 h-8" : "w-16 h-16";
  return (
    <motion.div
      className={`relative ${sizeClass}`}
      animate={isWheeling ? { rotate: [-5, 5, -5] } : {}}
      transition={{ duration: 0.15, repeat: isWheeling ? 3 : 0 }}
    >
      {/* Smoke/Exhaust effect when loading */}
      {isLoading && (
        <>
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute left-0 bottom-2 w-2 h-2 rounded-full bg-muted-foreground/40"
              initial={{ opacity: 0.8, scale: 0.5, x: 0, y: 0 }}
              animate={{
                opacity: [0.8, 0.4, 0],
                scale: [0.5, 1.5, 2],
                x: [-5, -20 - i * 8],
                y: [0, -5 - i * 3, -10 - i * 5],
              }}
              transition={{
                duration: 1,
                repeat: Infinity,
                delay: i * 0.2,
                ease: "easeOut",
              }}
            />
          ))}
        </>
      )}

      {/* MotoGP SVG Icon */}
      <motion.svg
        viewBox="0 0 64 64"
        className="w-full h-full"
        animate={
          isLoading
            ? { x: [0, 1, -1, 1, 0] }
            : isWheeling
            ? { rotate: [0, -15, 0] }
            : {}
        }
        transition={
          isLoading
            ? { duration: 0.1, repeat: Infinity }
            : { duration: 0.4 }
        }
      >
        {/* Rear wheel */}
        <motion.circle
          cx="14"
          cy="44"
          r="10"
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="3"
          animate={isLoading ? { rotate: 360 } : {}}
          transition={{ duration: 0.3, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: "14px 44px" }}
        />
        <circle cx="14" cy="44" r="3" fill="hsl(var(--primary))" />
        
        {/* Front wheel */}
        <motion.circle
          cx="50"
          cy="44"
          r="10"
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="3"
          animate={isLoading ? { rotate: 360 } : {}}
          transition={{ duration: 0.3, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: "50px 44px" }}
        />
        <circle cx="50" cy="44" r="3" fill="hsl(var(--primary))" />
        
        {/* Frame/Body */}
        <path
          d="M14 44 L22 28 L45 28 L50 44"
          fill="none"
          stroke="hsl(var(--foreground))"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        {/* Tank */}
        <ellipse
          cx="32"
          cy="26"
          rx="10"
          ry="6"
          fill="hsl(var(--primary))"
        />
        
        {/* Seat */}
        <path
          d="M25 26 L18 32 L25 32 Z"
          fill="hsl(var(--muted))"
        />
        
        {/* Fairing */}
        <path
          d="M42 28 L55 25 L58 30 L52 35 L45 32 Z"
          fill="hsl(var(--primary))"
        />
        
        {/* Number 58 */}
        <text
          x="30"
          y="29"
          fontSize="8"
          fontWeight="bold"
          fill="hsl(var(--primary-foreground))"
          textAnchor="middle"
        >
          58
        </text>
        
        {/* Rider silhouette */}
        <circle cx="26" cy="18" r="4" fill="hsl(var(--foreground))" />
        <path
          d="M24 22 L22 28 M28 22 L32 26"
          stroke="hsl(var(--foreground))"
          strokeWidth="2"
          strokeLinecap="round"
        />
        
        {/* Handlebar */}
        <line
          x1="48"
          y1="26"
          x2="52"
          y2="22"
          stroke="hsl(var(--muted-foreground))"
          strokeWidth="2"
          strokeLinecap="round"
        />
        
        {/* Exhaust pipe */}
        <path
          d="M10 40 L4 38 L2 42"
          fill="none"
          stroke="hsl(var(--muted-foreground))"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </motion.svg>

      {/* Neon glow effect */}
      <div 
        className="absolute inset-0 rounded-full opacity-50 blur-md pointer-events-none"
        style={{
          background: `radial-gradient(circle, hsl(var(--primary) / 0.3) 0%, transparent 70%)`,
        }}
      />
    </motion.div>
  );
}
